// src/components/PrivacyPolicy.tsx
import React from 'react';

const PrivacyPolicy: React.FC = () => {
  React.useEffect(() => {
    document.title = 'Privacy Policy — Inner Circle Personal CRM';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://withincircle.com/privacy-policy');
    }
    return () => {
      document.title = 'Inner Circle — Free AI Personal CRM & Contact List Tool | Voice & Chat';
    };
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md my-8">
      <h1 className="text-3xl font-bold mb-4 text-gray-800">Privacy Policy for Inner Circle</h1>
      <p className="text-gray-600 mb-8">Last Updated: April 27, 2026</p>
      
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
        <p className="font-medium">Summary</p>
        <p className="text-sm text-gray-600">
          We collect contact information you provide, usage data, and information needed to send reminders.
          We never sell your data and only use it to provide and improve our service. You can access, export,
          or delete your data at any time.
        </p>
      </div>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">1. Introduction</h2>
        <p className="mb-4 text-gray-700">
          Welcome to Inner Circle ("we," "our," or "us"). We respect your privacy and are committed to protecting 
          your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
          when you use our application and services.
        </p>
        <p className="mb-4 text-gray-700">
          By using Inner Circle, you consent to the data practices described in this Privacy Policy. If you do not agree 
          with the terms of this Privacy Policy, please do not access or use our application.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">2. Information We Collect</h2>
        
        <h3 className="text-xl font-medium mb-2 text-gray-800">2.1 Information You Provide</h3>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li>Account information (name, email address, profile picture)</li>
          <li>Contact information you input into the app (names, email addresses, phone numbers, organizations, etc.)</li>
          <li>Group information you create (group names, members, descriptions)</li>
          <li>Interactions and todos you record (meeting notes, call logs, follow-up items)</li>
          <li>Content for email reminders</li>
          <li>Any other information you voluntarily provide</li>
        </ul>

        <h3 className="text-xl font-medium mb-2 text-gray-800">2.1.1 Information from Connected Accounts</h3>
        <p className="mb-4 text-gray-700">
          When you connect a Google account in Settings → Connected Accounts, Inner Circle accesses
          a limited subset of data from that account to power calendar and email interaction tracking:
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li><span className="font-medium">Google Calendar:</span> event titles, times, locations, descriptions, and attendee email addresses on your primary calendar.</li>
          <li><span className="font-medium">Gmail (metadata only):</span> message headers — From, To, Cc, Subject, Date, and List-Unsubscribe — for messages in the last 90 days and going forward. <span className="font-medium">We never read message bodies.</span></li>
          <li><span className="font-medium">Account identity:</span> the email address and display name of the connected Google account, used to label the connection in the UI and identify which account owns each interaction record.</li>
        </ul>
        <p className="mb-4 text-gray-700">
          Calendar events and Gmail metadata are matched against the contacts you have already added
          to Inner Circle. When a match is found, an interaction record is created automatically so
          that you can see when you last communicated with that contact. Unmatched messages and
          events are not stored.
        </p>

        <h3 className="text-xl font-medium mb-2 text-gray-800">2.2 Information Collected Automatically</h3>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li>Usage data (features used, time spent, actions taken)</li>
          <li>Log data (IP address, browser type, operating system, access times)</li>
          <li>Device information (device type, mobile device identifiers)</li>
          <li>Error logs and performance data</li>
          <li>Analytics information through Google Analytics (anonymized)</li>
        </ul>

        <h3 className="text-xl font-medium mb-2 text-gray-800">2.3 Cookies and Similar Technologies</h3>
        <p className="mb-4 text-gray-700">
          We use cookies and similar tracking technologies to collect and track information about your interactions with our application. 
          These technologies may collect information such as your IP address, browser type, access times, pages viewed, and links clicked.
        </p>
        <p className="mb-4 text-gray-700">
          We use both session cookies (which expire when you close your browser) and persistent cookies (which remain on your device 
          until you delete them). You can control cookies through your browser settings and other tools.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">3. Legal Basis for Processing</h2>
        <p className="mb-4 text-gray-700">We process your personal data on the following legal bases:</p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li><span className="font-medium">Consent:</span> You have given consent for processing your data for specific purposes.</li>
          <li><span className="font-medium">Contract:</span> Processing is necessary to provide the services you've requested.</li>
          <li><span className="font-medium">Legitimate Interests:</span> Processing is necessary for our legitimate interests, such as improving our services, as long as they're not overridden by your interests or fundamental rights.</li>
          <li><span className="font-medium">Legal Obligation:</span> Processing is necessary to comply with legal obligations.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">4. How We Use Your Information</h2>
        <p className="mb-4 text-gray-700">We use your information for the following purposes:</p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li>To provide, operate, and maintain the Inner Circle service</li>
          <li>To send email reminders on your behalf (only with your explicit permission)</li>
          <li>To improve and personalize your experience</li>
          <li>To analyze usage patterns and optimize our application</li>
          <li>To communicate with you about updates, security alerts, and support</li>
          <li>To respond to your requests and communications</li>
          <li>To detect, prevent, and address technical issues or fraudulent activities</li>
          <li>To comply with legal obligations</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">5. Data Retention</h2>
        <p className="mb-4 text-gray-700">
          We retain your personal data for as long as necessary to fulfill the purposes outlined in this 
          Privacy Policy, unless a longer retention period is required or permitted by law.
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li><span className="font-medium">Account Information:</span> Retained as long as your account is active. Upon deletion of your account, we will delete or anonymize your account information within 30 days.</li>
          <li><span className="font-medium">Contact and Group Data:</span> Retained as long as you maintain this information in the application.</li>
          <li><span className="font-medium">Usage Data:</span> Retained for up to 24 months for analytics purposes.</li>
          <li><span className="font-medium">Log Data:</span> Retained for up to 90 days for security and troubleshooting purposes.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">6. Data Sharing and Disclosure</h2>
        <p className="mb-4 text-gray-700">
          We do not sell your personal information. We may share information in the following circumstances:
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li><span className="font-medium">With Your Consent:</span> When you have explicitly given us permission to do so.</li>
          <li><span className="font-medium">Service Providers:</span> With third-party service providers who help us operate, analyze, and improve our application (e.g., cloud hosting providers, analytics services).</li>
          <li><span className="font-medium">Legal Requirements:</span> To comply with applicable law, regulation, legal process, or governmental request.</li>
          <li><span className="font-medium">Protection of Rights:</span> To enforce our Terms of Service, protect the security or integrity of our services, or protect the rights, property, or safety of Inner Circle, our users, or others.</li>
          <li><span className="font-medium">Business Transfers:</span> In connection with a merger, acquisition, reorganization, or sale of assets.</li>
        </ul>
        
        <h3 className="text-xl font-medium mb-2 text-gray-800">6.1 Third-Party Service Providers</h3>
        <p className="mb-4 text-gray-700">
          We work with the following categories of third-party service providers:
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li>Google Cloud Platform, Heroku, and Neon (hosting and database infrastructure)</li>
          <li>Google Analytics (usage analytics)</li>
          <li>Google APIs — Gmail (send + metadata) and Google Calendar — for the connected-account features described in section 8</li>
          <li>OpenAI (AI assistant and speech-to-text features; only the inputs you submit are sent)</li>
          <li>Stripe (subscription payment processing)</li>
          <li>Telegram (optional bot integration if you connect it)</li>
        </ul>
        <p className="mb-4 text-gray-700">
          All service providers are data processors acting on our behalf and are contractually obligated to handle 
          your data securely and confidentially.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">7. Data Security</h2>
        <p className="mb-4 text-gray-700">
          We implement technical security measures to protect your personal information against
          unauthorized access, alteration, disclosure, or destruction. These measures include:
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li>Data encryption in transit (TLS/SSL) and at rest</li>
          <li>Access controls on the database and API surface</li>
          <li>OAuth tokens stored encrypted; revoked and deleted on disconnect</li>
          <li>Hosted on managed cloud infrastructure with regular platform-level patching</li>
        </ul>
        <p className="mb-4 text-gray-700">
          No method of transmission over the Internet or electronic storage is 100% secure. While we
          take reasonable measures to protect your information, we cannot guarantee absolute security.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">8. Google API Usage and Limited Use</h2>
        <p className="mb-4 text-gray-700 font-medium">
          Inner Circle's use and transfer of information received from Google APIs to any other app
          will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google
          API Services User Data Policy</a>, including the Limited Use requirements.
        </p>

        <h3 className="text-xl font-medium mb-2 text-gray-800">8.1 Scopes We Request</h3>
        <p className="mb-4 text-gray-700">
          When you connect a Google account, you may grant the following OAuth scopes:
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li>
            <span className="font-mono text-sm">gmail.send</span> — sends email reminders and AI-drafted
            messages on your behalf, only when you explicitly trigger them.
          </li>
          <li>
            <span className="font-mono text-sm">gmail.metadata</span> — reads message headers
            (From, To, Cc, Subject, Date, List-Unsubscribe) so Inner Circle can automatically log
            email exchanges with the contacts you have added. <span className="font-medium">Message
            bodies are never accessed.</span>
          </li>
          <li>
            <span className="font-mono text-sm">calendar</span> and <span className="font-mono text-sm">calendar.events</span> —
            reads your primary calendar so Inner Circle can log meetings with attendees who match
            your contacts, and (with your action) creates new events on your behalf.
          </li>
          <li>
            <span className="font-mono text-sm">userinfo.email</span> and <span className="font-mono text-sm">userinfo.profile</span> —
            identifies which Google account you authorized so we can dedupe and label connections
            when you connect more than one account.
          </li>
        </ul>

        <h3 className="text-xl font-medium mb-2 text-gray-800">8.2 How We Use Google Data</h3>
        <p className="mb-4 text-gray-700">
          Information obtained from Google APIs is used <span className="font-medium">solely</span> to
          provide the user-facing features of Inner Circle: sending emails you request, surfacing
          calendar context, and automatically tracking when you last communicated with each contact.
        </p>
        <p className="mb-4 text-gray-700">In particular, in line with the Limited Use requirements:</p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li>Google user data is <span className="font-medium">not</span> used to serve advertising.</li>
          <li>Google user data is <span className="font-medium">not</span> sold or transferred to third
            parties for purposes unrelated to providing Inner Circle's features.</li>
          <li>Google user data is <span className="font-medium">not</span> used to train generalized
            artificial intelligence or machine learning models.</li>
          <li>Humans do not read your Google data, except (a) you, (b) Inner Circle support staff
            with your explicit consent during a troubleshooting request, (c) where required by law,
            (d) for security investigations into abuse, or (e) to maintain and improve service
            functionality (for example, debugging an error you reported).</li>
        </ul>

        <h3 className="text-xl font-medium mb-2 text-gray-800">8.3 What We Store</h3>
        <p className="mb-4 text-gray-700">
          For each email or calendar event that matches one of your contacts, Inner Circle stores
          an interaction record containing: the date of the message or meeting, its subject or
          title, its direction (inbound or outbound for emails), and a reference to the originating
          Google message or event ID (used for deduplication and to fetch the latest state).
          Unmatched messages and events are not stored. Email message bodies are never stored.
        </p>

        <h3 className="text-xl font-medium mb-2 text-gray-800">8.4 Revoking Access</h3>
        <p className="mb-4 text-gray-700">
          You can disconnect any connected Google account at any time from Settings → Connected
          Accounts inside Inner Circle. Disconnecting revokes the OAuth refresh token, deletes the
          stored credentials, and stops all further sync from that account. You can also revoke
          access directly through your <a href="https://myaccount.google.com/permissions"
            target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google
          Account permissions page</a>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">9. International Data Transfers</h2>
        <p className="mb-4 text-gray-700">
          Your information may be transferred to and processed in countries other than the country in which you reside. 
          These countries may have data protection laws that differ from your country.
        </p>
        <p className="mb-4 text-gray-700">
          Inner Circle is hosted on managed cloud infrastructure operated by third parties. The
          physical location of the servers depends on those providers and may be in the United
          States or other regions they operate in. By using Inner Circle, you consent to the
          transfer of your information to these locations.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">10. Your Rights and Choices</h2>
        <p className="mb-4 text-gray-700">Depending on your location, you may have certain rights regarding your personal information:</p>
        
        <h3 className="text-xl font-medium mb-2 text-gray-800">10.1 For All Users</h3>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li><span className="font-medium">Access:</span> View the personal information we hold about you</li>
          <li><span className="font-medium">Correction:</span> Correct inaccurate information</li>
          <li><span className="font-medium">Deletion:</span> Request deletion of your data</li>
          <li><span className="font-medium">Data Export:</span> Export your data in a structured format</li>
          <li><span className="font-medium">Withdrawal of Consent:</span> Withdraw consent for optional features like email notifications</li>
        </ul>
        
        <h3 className="text-xl font-medium mb-2 text-gray-800">10.2 For EEA/UK/Switzerland Residents (GDPR)</h3>
        <p className="mb-4 text-gray-700">
          If you reside in the EEA, UK, or Switzerland, you have additional rights under the General Data Protection Regulation:
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li><span className="font-medium">Right to Object:</span> Object to processing based on legitimate interests</li>
          <li><span className="font-medium">Restriction of Processing:</span> Request restriction of processing in certain circumstances</li>
          <li><span className="font-medium">Data Portability:</span> Receive your data in a machine-readable format</li>
          <li><span className="font-medium">Complaint:</span> Lodge a complaint with a supervisory authority</li>
        </ul>
        
        <h3 className="text-xl font-medium mb-2 text-gray-800">10.3 For California Residents (CCPA/CPRA)</h3>
        <p className="mb-4 text-gray-700">
          If you reside in California, you have rights under the California Consumer Privacy Act (CCPA) and 
          California Privacy Rights Act (CPRA):
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li><span className="font-medium">Right to Know:</span> Request information about the personal information we collect, use, and disclose</li>
          <li><span className="font-medium">Right to Delete:</span> Request deletion of your personal information</li>
          <li><span className="font-medium">Right to Opt-Out:</span> Opt out of the sale or sharing of personal information (note that we do not sell personal information)</li>
          <li><span className="font-medium">Right to Non-Discrimination:</span> Not be discriminated against for exercising these rights</li>
        </ul>
        
        <h3 className="text-xl font-medium mb-2 text-gray-800">10.4 How to Exercise Your Rights</h3>
        <p className="mb-4 text-gray-700">
          To exercise your rights:
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li><span className="font-medium">In-App:</span> Use the privacy settings in the application to access, export, or delete your data</li>
          <li><span className="font-medium">Email:</span> Contact us at <a href="mailto:evs.abyss@gmail.com" className="text-blue-600 hover:underline">evs.abyss@gmail.com</a></li>
          <li><span className="font-medium">Verification:</span> We may need to verify your identity before fulfilling your request</li>
          <li><span className="font-medium">Response Time:</span> We will respond to your request within 30 days</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">11. Children's Privacy</h2>
        <p className="mb-4 text-gray-700">
          Our service is not directed to children under 13 (or 16 in the European Union). We do not knowingly collect 
          personal information from children under these ages. If we learn we have collected personal 
          information from a child without parental consent, we will take steps to delete that information.
        </p>
        <p className="mb-4 text-gray-700">
          If you believe we might have any information from or about a child under these ages, please contact us at 
          <a href="mailto:evs.abyss@gmail.com" className="text-blue-600 hover:underline"> evs.abyss@gmail.com</a>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">12. Breach Notification</h2>
        <p className="mb-4 text-gray-700">
          In the event of a data breach that compromises your personal information, we will:
        </p>
        <ul className="list-disc pl-8 mb-4 text-gray-700 space-y-1">
          <li>Notify affected users within 72 hours of becoming aware of the breach, where feasible</li>
          <li>Provide information about the nature of the breach, the information affected, and steps we are taking</li>
          <li>Take immediate steps to contain and investigate the breach</li>
          <li>Notify relevant authorities as required by applicable law</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">13. Dispute Resolution</h2>
        <p className="mb-4 text-gray-700">
          If you have a concern about our privacy practices, please contact us first at 
          <a href="mailto:evs.abyss@gmail.com" className="text-blue-600 hover:underline"> evs.abyss@gmail.com</a> 
          to attempt to resolve the matter directly.
        </p>
        <p className="mb-4 text-gray-700">
          For unresolved complaints, you may have the right to pursue other legal remedies, including filing a complaint 
          with your local data protection authority.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">14. Changes to This Privacy Policy</h2>
        <p className="mb-4 text-gray-700">
          We may update this Privacy Policy from time to time. The updated version will be indicated by an updated 
          "Last Updated" date and will be effective as soon as it is accessible.
        </p>
        <p className="mb-4 text-gray-700">
          We encourage you to review this Privacy Policy regularly to stay informed about our information practices. 
          If we make material changes, we will notify you through the application or by email prior to the change becoming effective.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">15. Contact Us</h2>
        <p className="mb-4 text-gray-700">
          If you have any questions about this Privacy Policy or our data practices, contact:
        </p>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
          <p className="mb-0"><a href="mailto:evs.abyss@gmail.com" className="text-blue-600 hover:underline">evs.abyss@gmail.com</a></p>
        </div>
      </section>

      <div className="border-t border-gray-200 pt-6 mt-10">
        <p className="text-sm text-gray-500 text-center">© Inner Circle</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;